import { clinicalLimitStatus, formatStructuredClinicalLimit } from './clinicalTargetSemantics';
import { suggestedRdaToneStyles } from './suggestedRdaTone';
import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import {
  buildConicGradient,
  formatMetric,
  getStatusColor,
  type AnalysisData,
} from './analysis.shared';

/** Figma node 97:3409 — energy bar + macro accents (presentation only). */
const FIGMA_MACRO = {
  carbs: '#2ec3b6',
  protein: '#20aedc',
  fat: '#3f9fee',
  fibre: '#f59e0b',
  sugar: '#ff8585',
} as const;

/** Figma 97:5687 / 97:5990 — analysis Macros tab (presentation only). */
export type MacroDistributionCardAppearance = 'default' | 'macroFigma';

interface MacroDistributionCardProps {
  analysisData: AnalysisData;
  freeSugarTarget: number;
  appearance?: MacroDistributionCardAppearance;
}

function normalizeMetricLabel(label: string): string {
  return label.trim().toLowerCase();
}

function EnergyDistributionSegment({
  widthPercent,
  bgcolor,
  title,
  pctLabel,
}: {
  widthPercent: number;
  bgcolor: string;
  title: string;
  pctLabel: string;
}) {
  const w = Math.max(0, widthPercent);
  if (!(w > 0)) return null;

  return (
    <Tooltip title={title} placement="top" arrow enterDelay={200}>
      <Box
        sx={{
          width: `${w}%`,
          minWidth: 0,
          height: '100%',
          flexShrink: 0,
          bgcolor,
          cursor: 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '15px',
            letterSpacing: '0.12px',
            color: '#fff',
            textAlign: 'center',
            px: 0.25,
            whiteSpace: 'nowrap',
          }}
        >
          {pctLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function buildMacroDistributionSubcomponentsLine(mdp: NonNullable<AnalysisData['macroDistributionPercent']>): string | null {
  const parts: string[] = [];
  if (mdp.sugar > 0) parts.push(`Sugar ${formatMetric(mdp.sugar, 1)}%`);
  if (mdp.dietaryFibre != null && mdp.dietaryFibre > 0) parts.push(`Fibre ${formatMetric(mdp.dietaryFibre, 1)}%`);
  if (mdp.mufa > 0) parts.push(`MUFA ${formatMetric(mdp.mufa, 1)}%`);
  if (mdp.pufa > 0) parts.push(`PUFA ${formatMetric(mdp.pufa, 1)}%`);
  return parts.length ? parts.join(' · ') : null;
}

export const MacroDistributionCard: React.FC<MacroDistributionCardProps> = ({
  analysisData,
  freeSugarTarget,
  appearance = 'default',
}) => {
  const isMacroFigma = appearance === 'macroFigma';
  const mdp = analysisData.macroDistributionPercent;
  const energyTotalKcal =
    mdp && mdp.denominatorKcal > 0 ? mdp.denominatorKcal : analysisData.energy;
  const macroSubcomponentsLine = mdp ? buildMacroDistributionSubcomponentsLine(mdp) : null;

  const fatProgress = (analysisData.fat / Math.max(1, analysisData.fatTarget)) * 100;
  const fatDisplayStatus = getStatusColor(fatProgress, {
    value: analysisData.fat,
    target: analysisData.fatTarget,
  });
  const nutrientCards = React.useMemo(() => {
    const excluded = new Set([
      'energy',
      'water',
      'fat',
      'total fat',
      'saturated fat',
      'sat fat',
      'alcohol',
      'cholesterol',
      'cholestrol',
      'sodium',
      'omega-3',
      'omega 3',
      'omega_3',
      'omega-6',
      'omega 6',
      'omega_6',
    ]);
    const kpiCards = analysisData.kpis
      .filter((kpi) => {
        const n = normalizeMetricLabel(kpi.label);
        if (excluded.has(n)) return false;
        if (n.includes('saturated')) return false;
        if (n.includes('trans')) return false;
        if (n.includes('free sugar')) return false;
        if (n.includes('alcohol') || n.includes('ethanol')) return false;
        return true;
      })
      .map((kpi, index) => ({
        id: `kpi-${normalizeMetricLabel(kpi.label) || index}`,
        label: kpi.label,
        value: kpi.value,
        target: kpi.target,
        unit: kpi.unit,
      }));

    if (kpiCards.length) return kpiCards;

    return [
      {
        id: 'carbohydrates',
        label: 'Carbohydrates',
        value: analysisData.carbs,
        target: analysisData.carbsTarget,
        unit: 'g',
      },
      {
        id: 'protein',
        label: 'Protein',
        value: analysisData.protein,
        target: analysisData.proteinTarget,
        unit: 'g',
      },
      {
        id: 'fibre',
        label: 'Fibre',
        value: analysisData.fibre,
        target: analysisData.fibreTarget,
        unit: 'g',
      },
    ];
  }, [analysisData.carbs, analysisData.carbsTarget, analysisData.fibre, analysisData.fibreTarget, analysisData.kpis, analysisData.protein, analysisData.proteinTarget]);

  const fatBreakdown = React.useMemo(() => {
    const ft = analysisData.fatSubtypeGrams;
    const transFatKpi = analysisData.kpis.find((k) => k.label.trim().toLowerCase() === 'trans fat');
    const transGrams = Math.max(
      0,
      ft != null && Number.isFinite(ft.trans)
        ? ft.trans
        : transFatKpi != null && Number.isFinite(transFatKpi.value)
          ? transFatKpi.value
          : 0,
    );

    const items = [
      { label: 'Monounsaturated', grams: ft?.monounsaturated ?? 0 },
      { label: 'Polyunsaturated', grams: ft?.polyunsaturated ?? 0 },
      { label: 'Saturated', grams: analysisData.saturatedFat ?? 0 },
      { label: 'Trans fat', grams: transGrams },
    ].filter((item) => item.grams > 0);
    if (!items.length) return [] as { label: string; grams: number; percent: number }[];
    // Use total fat grams as denominator so subtype percentages don't inflate
    // when backend subtype groups overlap (e.g. saturated reported alongside other groups).
    const totalFatGrams = Math.max(0, Number(analysisData.fat) || 0);
    const fallbackTotal = items.reduce((sum, item) => sum + item.grams, 0);
    const denominator = totalFatGrams > 0 ? totalFatGrams : fallbackTotal;

    return items.map((item) => ({
      ...item,
      percent: denominator > 0 ? (item.grams / denominator) * 100 : 0,
    }));
  }, [analysisData.fatSubtypeGrams, analysisData.saturatedFat, analysisData.fat, analysisData.kpis]);

  /** Same intake-vs-target ring as nutrient cards — Suggested RDA tone via `getStatusColor`. */
  const fatRingGradient = React.useMemo(() => {
    const p = Math.min(100, Math.max(0, fatProgress));
    return buildConicGradient([
      { value: p, color: fatDisplayStatus.color },
      { value: Math.max(0, 100 - p), color: '#e4e7ec' },
    ]);
  }, [fatProgress, fatDisplayStatus.color]);

  const energyDistributionSegments = React.useMemo(() => {
    const pctLabel = (n: number) => `${formatMetric(n, 0)}%`;
    type EnergyBarSeg = { key: string; width: number; color: string; title: string };
    const hasMacroIntake = analysisData.carbs > 0 || analysisData.protein > 0 || analysisData.fat > 0;
    const hasMdpCoreShare = !!mdp && (mdp.carbohydrate > 0 || mdp.protein > 0 || mdp.totalFat > 0);
    const shouldUseMdp = !!mdp && hasMdpCoreShare && (analysisData.energy > 0 || hasMacroIntake);
    if (shouldUseMdp && mdp) {
      const core: EnergyBarSeg[] = [
        {
          key: 'carbs',
          width: mdp.carbohydrate,
          color: FIGMA_MACRO.carbs,
          title: '',
        },
        {
          key: 'protein',
          width: mdp.protein,
          color: FIGMA_MACRO.protein,
          title: '',
        },
        {
          key: 'fat',
          width: mdp.totalFat,
          color: FIGMA_MACRO.fat,
          title: '',
        },
      ];
      if (mdp.dietaryFibre != null && mdp.dietaryFibre > 0) {
        core.push({
          key: 'fibre',
          width: mdp.dietaryFibre,
          color: FIGMA_MACRO.fibre,
          title: '',
        });
      }
      // Use API `macroDistributionPercent` shares as-is (no rescaling to 100% of the bar).
      const macroTitle = (key: string, width: number): string => {
        const pct = formatMetric(width, 1);
        if (key === 'carbs') return `Carbohydrates — ${pct}% of energy`;
        if (key === 'protein') return `Protein — ${pct}% of energy`;
        if (key === 'fibre') return `Fibre — ${pct}% of energy`;
        return `Fat — ${pct}% of energy`;
      };
      return core.map((s) => ({
        ...s,
        title: macroTitle(s.key, s.width),
        pctLabel: pctLabel(s.width),
      }));
    }
    return [
      {
        key: 'carbs',
        width: analysisData.macroShare.carbs,
        color: FIGMA_MACRO.carbs,
        title: `Carbohydrates — ${formatMetric(analysisData.macroShare.carbs, 1)}% of energy`,
      },
      {
        key: 'protein',
        width: analysisData.macroShare.protein,
        color: FIGMA_MACRO.protein,
        title: `Protein — ${formatMetric(analysisData.macroShare.protein, 1)}% of energy`,
      },
      {
        key: 'fat',
        width: analysisData.macroShare.fat,
        color: FIGMA_MACRO.fat,
        title: `Fat — ${formatMetric(analysisData.macroShare.fat, 1)}% of energy`,
      },
    ].map((s) => ({ ...s, pctLabel: pctLabel(s.width) }));
  }, [
    mdp,
    analysisData.macroShare.carbs,
    analysisData.macroShare.protein,
    analysisData.macroShare.fat,
    analysisData.carbsKcal,
    analysisData.proteinKcal,
    analysisData.fatKcal,
    analysisData.energy,
  ]);

  const energyLegendLabels: Record<string, string> = {
    carbs: 'Carbohydrates',
    protein: 'Protein',
    fat: 'Fat',
    fibre: 'Fibre',
    sugar: 'Sugar',
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid #f3f4f6',
        bgcolor: '#fff',
        p: '21px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '9px',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '18px',
          lineHeight: '27px',
          letterSpacing: '-0.44px',
          color: '#1e2939',
        }}
      >
        Macronutrient Distribution
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
          color: '#99a1af',
        }}
      >
        Current intake breakdown vs targets
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '16px', color: '#99a1af' }}>
            Energy Distribution (% kcal)
          </Typography>
        
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', width: '100%' }}>
          <Box
            sx={{
              width: '100%',
              height: 31,
              borderRadius: '10px',
              overflow: 'hidden',
              display: 'flex',
              gap: 0,
              // Track color shows any remainder when API % shares do not sum to 100%.
              bgcolor: '#f1f5f9',
            }}
          >
            {energyDistributionSegments.map((seg) => (
              <EnergyDistributionSegment
                key={seg.key}
                widthPercent={seg.width}
                bgcolor={seg.color}
                title={seg.title}
                pctLabel={seg.pctLabel}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '16px', rowGap: 0.5 }}>
            {energyDistributionSegments.map((seg) => (
              <Box key={`leg-${seg.key}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '6px', bgcolor: seg.color, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '15px',
                    letterSpacing: '0.12px',
                    color: '#6a7282',
                  }}
                >
                  {energyLegendLabels[seg.key] ?? seg.key}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {macroSubcomponentsLine ? (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3', lineHeight: 1.35 }}>
            {macroSubcomponentsLine} <Box component="span" sx={{ color: '#b4bcc8' }}>(% of total energy)</Box>
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          mt: 0.5,
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          width: '100%',
        }}
      >
        {nutrientCards.map((card) => {
          const normalizedLabel = normalizeMetricLabel(card.label);
          const progress = card.target > 0 ? (card.value / card.target) * 100 : 0;
          const isMinimumTarget = normalizedLabel.includes('fibre') || normalizedLabel === 'omega-3';
          const limitKey = normalizedLabel.includes('fibre') ? 'fibre' : normalizedLabel.includes('free sugar') ? 'freeSugars' : normalizedLabel.includes('saturated') ? 'saturatedFat' : normalizedLabel.includes('fat') ? 'fat' : normalizedLabel === 'protein' ? 'protein' : normalizedLabel.includes('carb') ? 'carbs' : undefined;
          const limit = limitKey ? analysisData.mealPlanClinicalLimits?.[limitKey] : undefined;
          const resolvedStatus = limit ? clinicalLimitStatus({ limit, intake: card.value }) : null;
          const status = resolvedStatus ? { color: suggestedRdaToneStyles[resolvedStatus.tone].bar, label: resolvedStatus.tone === 'met' ? 'Met' : resolvedStatus.tone === 'deficient' ? 'Deficient' : 'Excess' } : getStatusColor(progress, {
            value: card.value,
            target: card.target,
            isMinimumTarget,
          });
          const ringAccent = status.color;
          const metricDecimals = card.unit === 'L' ? 1 : normalizedLabel === 'omega-3' ? 2 : 0;

          return (
            <Box
              key={card.id}
              sx={{
                borderRadius: '12px',
                border: isMacroFigma ? '1px solid #eeeeee' : '1px solid #eee',
                boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.09)',
                px: '12px',
                py: '16px',
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gap: '16px',
                alignItems: 'center',
                bgcolor: '#fff',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: buildConicGradient([
                    { value: Math.min(100, Math.max(0, progress)), color: ringAccent },
                    { value: Math.max(0, 100 - progress), color: '#e4e7ec' },
                  ]),
                  p: '4px',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    bgcolor: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: '12px',
                    letterSpacing: '0.17px',
                    color: ringAccent,
                  }}
                >
                  {formatMetric(Math.min(999, progress), 0)}%
                </Box>
              </Box>

              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.15px',
                    color: '#1e2939',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {card.label}
                </Typography>
                {limit && <Typography sx={{ fontSize: 12 }}>Clinical limit: {formatStructuredClinicalLimit(limit)}</Typography>}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '16px', color: '#99a1af' }}>
                    {formatMetric(card.value, metricDecimals)} / {isMinimumTarget ? '≥' : ''}{formatMetric(card.target, metricDecimals)}
                    {card.unit}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '16px', fontWeight: 700, color: '#717182' }}>
                    {formatMetric(progress, 0)}%
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: status.color, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: status.color,
                      }}
                    >
                      {status.label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}

        <Box
          sx={{
            gridColumn: '1 / -1',
            borderRadius: '12px',
            border: isMacroFigma ? '1px solid #eeeeee' : '1px solid #eee',
            boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.09)',
            px: '12px',
            py: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-start',
            bgcolor: '#fff',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', flex: '1 1 220px', minWidth: 0 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: fatRingGradient,
                p: '4px',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  bgcolor: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '12px',
                    letterSpacing: '0.17px',
                    color: fatDisplayStatus.color,
                  }}
                >
                  {formatMetric(fatProgress, 0)}%
                </Typography>
              </Box>
            </Box>

            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '-0.15px',
                  color: '#1e2939',
                }}
              >
                Total Fat
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap', fontSize: '12px', lineHeight: '16px' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, color: '#99a1af' }}>
                  {analysisData.fat} / {formatMetric(analysisData.fatTarget, 0)}g
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#717182' }}>
                  {formatMetric(fatProgress, 0)}%
                </Typography>
              </Box>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: fatDisplayStatus.color, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: fatDisplayStatus.color,
                  }}
                >
                  {fatDisplayStatus.label}
                </Typography>
              </Box>
            </Box>
          </Box>

          {fatBreakdown.length ? (
            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                flex: '2 1 260px',
                minWidth: 0,
              }}
            >
              {fatBreakdown.map((item) => (
                <Typography
                  key={item.label}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#374151',
                    minWidth: 0,
                  }}
                >
                  {item.label}{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>
                    {formatMetric(item.grams, 1)}g
                  </Box>
                  <Box component="span" sx={{ color: '#9ca3af' }}>
                    {' '}
                    ({formatMetric(item.percent, 0)}%)
                  </Box>
                </Typography>
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
};
